import React, { useState, useEffect } from 'react';
import { FaDownload, FaCheck, FaSpinner, FaTimes, FaComment, FaHistory, FaEye, FaTrashAlt } from 'react-icons/fa';
import './ReportSubmission.css';
import { API_URL } from './API';

const ReportSubmission = ({ onBack, projectId }) => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [remarkVersions, setRemarkVersions] = useState({});
  const [showVersionHistory, setShowVersionHistory] = useState({});
  const [currentRemark, setCurrentRemark] = useState({});

  useEffect(() => {
    if (projectId) {
      fetchUploadedReports();
    }
  }, [projectId]);

  const fetchUploadedReports = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const url = `${API_URL}/user_uploads/get_user_uploads?project_id=${projectId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch reports: ${response.status}`);
      }

      const result = await response.json();

      let reportsData = [];
      if (result.status === 'success' && result.data) {
        reportsData = Array.isArray(result.data) ? result.data : [result.data];
      } else if (Array.isArray(result)) {
        reportsData = result;
      }

      const formattedReports = reportsData.map(report => ({
        id: report.user_upload_id || report.id,
        name: extractOriginalFileName(report.user_upload_file) || 'Unnamed Report',
        serverFileName: extractFileName(report.user_upload_file), // Keep server filename for API calls
        file_url: report.user_upload_file,
        uploaded_date: report.created_on || report.updated_on,
        status: 'Uploaded',
        is_seen: report.is_seen || false
      }));

      setReports(formattedReports);
      
      formattedReports.forEach(report => {
        fetchRemarkVersions(report.id);
      });

      const unseenReports = formattedReports.filter(report => !report.is_seen);
      if (unseenReports.length > 0) {
        for (const unseenReport of unseenReports) {
          await markReportAsSeen(unseenReport.id);
        }
      }
    } catch (err) {
      setError('Failed to load reports. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRemarkVersions = async (reportId) => {
    try {
      const response = await fetch(`${API_URL}/user_uploads/get_remarks?user_upload_id=${reportId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        if (response.status === 400) {
          return;
        }
        throw new Error('Failed to fetch remark versions');
      }

      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        const versions = Array.isArray(result.data) ? result.data : [result.data];
        setRemarkVersions(prev => ({
          ...prev,
          [reportId]: versions.sort((a, b) => b.version - a.version)
        }));
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const markReportAsSeen = async (reportId) => {
    try {
      const formData = new FormData();
      formData.append('user_upload_id', reportId);

      const response = await fetch(`${API_URL}/user_uploads/mark_as_seen`, {
        method: 'POST',
        mode: 'cors',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to mark as seen');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setReports(prevReports =>
          prevReports.map(report =>
            report.id === reportId ? { ...report, is_seen: true } : report
          )
        );
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const extractFileName = (filePath) => {
    if (!filePath) return 'Unnamed Report';
    const parts = filePath.split('/');
    const filename = parts[parts.length - 1];
    return filename;
  };

  const extractOriginalFileName = (filePath) => {
    if (!filePath) return 'Unnamed Report';
    const parts = filePath.split('/');
    const filename = parts[parts.length - 1];
    
    // Remove timestamp pattern: _20260112125731_8480f3f0
    // Pattern matches: underscore + 14 digits + underscore + 8+ hex characters
    const cleanedName = filename.replace(/_\d{14}_[a-f0-9]{8,}/i, '');
    
    return cleanedName;
  };

  const handleRemarksClick = (reportId, e) => {
    e.stopPropagation();
    
    if (expandedReportId === reportId) {
      setExpandedReportId(null);
      setShowVersionHistory(prev => ({ ...prev, [reportId]: false }));
    } else {
      setExpandedReportId(reportId);
      setCurrentRemark(prev => ({ ...prev, [reportId]: prev[reportId] || '' }));
      setShowVersionHistory(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleRemarksChange = (id, value) => {
    setCurrentRemark(prev => ({ ...prev, [id]: value }));
  };

  const handleDownload = async (report, e) => {
    e.stopPropagation();
    
    if (!report.file_url) {
      alert('File URL not available');
      return;
    }

    try {
      const downloadUrl = `${API_URL}/user_uploads/download_user_upload/${report.serverFileName}`;
      
      // Fetch the file and download with original name
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.name; // Use cleaned original name
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download file: ' + err.message);
    }
  };

  const createDocumentViewer = (url, filename, fileExt) => {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      flex-direction: column;
    `;
    
    const header = document.createElement('div');
    header.style.cssText = `
      background-color: #2c3e50;
      color: white;
      padding: 15px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    
    const title = document.createElement('h3');
    title.textContent = filename;
    title.style.margin = '0';
    title.style.fontSize = '16px';
    
    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      font-size: 24px;
      cursor: pointer;
      padding: 0;
      width: 30px;
      height: 30px;
    `;
    closeButton.onclick = () => document.body.removeChild(modal);
    
    header.appendChild(title);
    header.appendChild(closeButton);
    
    const iframe = document.createElement('iframe');
    
    const officeFormats = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    if (officeFormats.includes(fileExt)) {
      iframe.src = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
    } else {
      iframe.src = url;
    }
    
    iframe.style.cssText = `
      flex: 1;
      border: none;
      width: 100%;
      background-color: white;
    `;
    
    const loadingMsg = document.createElement('div');
    loadingMsg.textContent = 'Loading document...';
    loadingMsg.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 18px;
    `;
    modal.appendChild(loadingMsg);
    
    iframe.onload = () => {
      if (loadingMsg.parentNode) {
        loadingMsg.remove();
      }
    };
    
    modal.appendChild(header);
    modal.appendChild(iframe);
    document.body.appendChild(modal);
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        document.body.removeChild(modal);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  };

  const handleViewReport = (report, e) => {
    e.stopPropagation();
    
    if (!report.file_url) {
      alert('File URL not available');
      return;
    }

    try {
      const fileExt = report.name.toLowerCase().split('.').pop();
      const viewUrl = `${API_URL}/user_uploads/view_user_upload/${report.serverFileName}`;
    
      const directViewFiles = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'txt', 'html', 'htm'];
      const officeFiles = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      
      if (directViewFiles.includes(fileExt)) {
        const newWindow = window.open(viewUrl, '_blank', 'noopener,noreferrer');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          alert('Pop-up blocked! Please allow pop-ups for this site to view files.');
        }
      } else if (officeFiles.includes(fileExt)) {
        createDocumentViewer(viewUrl, report.name, fileExt);
      } else {
        if (window.confirm(`This file type (.${fileExt}) cannot be previewed. Would you like to download it instead?`)) {
          handleDownload(report, e);
        }
      }
    } catch (err) {
      alert('Failed to open file: ' + err.message);
    }
  };

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this report? This will also delete all associated remarks.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user_uploads/delete_user_upload?user_upload_id=${reportId}`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete report');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setReports(prevReports => prevReports.filter(report => report.id !== reportId));
        setRemarkVersions(prev => {
          const newVersions = { ...prev };
          delete newVersions[reportId];
          return newVersions;
        });
        setCurrentRemark(prev => {
          const newRemarks = { ...prev };
          delete newRemarks[reportId];
          return newRemarks;
        });
        if (expandedReportId === reportId) {
          setExpandedReportId(null);
        }
        alert('Report deleted successfully!');
      } else {
        throw new Error(result.message || 'Failed to delete report');
      }
    } catch (err) {
      alert('Failed to delete report: ' + err.message);
    }
  };

  const handleDeleteRemark = async (reportId, remarkId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this remark version?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user_uploads/delete_remark/${remarkId}`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete remark');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchRemarkVersions(reportId);
        alert('Remark deleted successfully!');
      } else {
        throw new Error(result.message || 'Failed to delete remark');
      }
    } catch (err) {
      alert('Failed to delete remark: ' + err.message);
    }
  };

  const handleSaveRemarks = async (reportId, e) => {
    e.stopPropagation();
    
    const remarkText = currentRemark[reportId] || '';
    
    if (!remarkText.trim()) {
      alert('Please enter a remark before saving');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('user_upload_id', reportId);
      formData.append('remarks', remarkText);

      const response = await fetch(`${API_URL}/user_uploads/create_remarks`, {
        method: 'POST',
        mode: 'cors',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to save remarks');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        alert(`Remarks saved successfully as Version ${result.data.version}!`);
        setCurrentRemark(prev => ({ ...prev, [reportId]: '' }));
        await fetchRemarkVersions(reportId);
      } else {
        throw new Error(result.message || 'Failed to save remarks');
      }
    } catch (err) {
      alert('Failed to save remarks: ' + err.message);
    }
  };

  const toggleVersionHistory = (reportId, e) => {
    e.stopPropagation();
    setShowVersionHistory(prev => ({ ...prev, [reportId]: !prev[reportId] }));
  };

  const SeenIndicator = ({ isSeen }) => (
    <div className="RS-00" title={isSeen ? "Seen" : "Not seen"}>
      <FaCheck size={14} className={isSeen ? "RS-01" : "RS-02"} />
      <FaCheck size={14} className={isSeen ? "RS-01 RS-03" : "RS-02 RS-03"} />
    </div>
  );

  return (
    <div className="RS-04">
      <div className="RS-05">
        <h2 className="RS-06">Uploaded Reports</h2>
        <button onClick={onBack} className="RS-07">
          ← Back
        </button>
      </div>

      {isLoading && (
        <div className="RS-08">
          <FaSpinner className="RS-09" />
          <p>Loading reports...</p>
        </div>
      )}

      {error && (
        <div className="RS-10">
          <FaTimes /> {error}
        </div>
      )}

      <div className="RS-11">
        {reports.map((report) => (
          <div key={report.id} className="RS-12">
            <div className="RS-13">
              <div className="RS-14">
                <div className="RS-15">
                  <h3 className="RS-16" title={report.name}>{report.name}</h3>
                  {report.uploaded_date && (
                    <p className="RS-17">
                      Uploaded: {new Date(report.uploaded_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="RS-18">
                  <button 
                    className="RS-19" 
                    onClick={(e) => handleRemarksClick(report.id, e)}
                    title="View Remarks"
                  >
                    <FaComment size={18} />
                  </button>
                  
                  <button 
                    className="RS-19" 
                    onClick={(e) => handleDownload(report, e)}
                    title="Download"
                  >
                    <FaDownload size={18} />
                  </button>
                  <button 
                    className="RS-19 RS-20" 
                    onClick={(e) => handleDeleteReport(report.id, e)}
                    title="Delete Report"
                  >
                    <FaTrashAlt size={18} />
                  </button>
                  <SeenIndicator isSeen={report.is_seen} />
                </div>
              </div>
            </div>

            {expandedReportId === report.id && (
              <div className="RS-21">
                <div className="RS-22">
                  <label className="RS-23">Add New Remark</label>
                  {remarkVersions[report.id] && remarkVersions[report.id].length > 0 && (
                    <button 
                      className="RS-24"
                      onClick={(e) => toggleVersionHistory(report.id, e)}
                    >
                      <FaHistory size={14} />
                      <span>Version History ({remarkVersions[report.id].length})</span>
                    </button>
                  )}
                </div>
                
                <textarea
                  className="RS-25"
                  value={currentRemark[report.id] || ''}
                  onChange={(e) => handleRemarksChange(report.id, e.target.value)}
                  placeholder="Enter your remark here..."
                  rows="4"
                  onClick={(e) => e.stopPropagation()}
                />
                
                <button 
                  className="RS-26" 
                  onClick={(e) => handleSaveRemarks(report.id, e)}
                  title="Save Remarks"
                >
                  <FaCheck size={16} /> Save Remark
                </button>

                {showVersionHistory[report.id] && remarkVersions[report.id] && (
                  <div className="RS-27">
                    <h4 className="RS-28">Previous Versions</h4>
                    {remarkVersions[report.id].map((version) => (
                      <div key={version.remark_id} className="RS-29">
                        <div className="RS-30">
                          <div className="RS-31">
                            <span className="RS-32">Version {version.version}</span>
                            <span className="RS-33">
                              {new Date(version.created_on).toLocaleString()}
                            </span>
                          </div>
                          <button 
                            className="RS-34"
                            onClick={(e) => handleDeleteRemark(report.id, version.remark_id, e)}
                            title="Delete this version"
                          >
                            <FaTrashAlt size={12} />
                          </button>
                        </div>
                        <p className="RS-35">{version.remarks}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!isLoading && reports.length === 0 && (
        <div className="RS-36">
          <p className="RS-37">No reports uploaded yet for this project.</p>
        </div>
      )}
    </div>
  );
};

export default ReportSubmission;